/** @jsx jsx */
import {
  ColumnConfig,
  CompanyAvatar,
  DottedList,
  DottedListItem,
  Flex,
  List,
  ListCellsWrapper,
  ListHeader,
  ListHeaderCell,
  ListItem,
  ListItemCell,
  Paragraph,
} from "@eworkit/iris";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components/avatars";
import { DateRange } from "@/components/dates";
import { UserContext } from "@/components/User";
import { formatFullName } from "@/data/persons";
import { formatTotalLoggedTime, TimeReportShort } from "@/data/time-reports";
import { useFormats } from "@/hooks/Formats";
import { usePreviousRoutePath } from "@/infrastructure/Route";
import { router } from "@/views/router";

import { TimeReportStageStatus } from "../stages";

interface TimeReportsListProps {
  timeReports: TimeReportShort[];
}

export const TimeReportsList: FC<TimeReportsListProps> = ({ timeReports }) => {
  const { t } = useTranslation("session");

  const { currentLocationState } = usePreviousRoutePath();

  const { hasCompanyManagerPrivileges } = useContext(UserContext);
  const isManager = hasCompanyManagerPrivileges();

  const { formatCost } = useFormats();

  const cols = useMemo(() => {
    const period: ColumnConfig = { sx: { width: [null, null, null, isManager ? "300px" : "425px"] } };

    const worker: ColumnConfig = { sx: { width: [null, null, null, "20%"] } };

    const client: ColumnConfig = { sx: { flexGrow: 1 } };

    const time: ColumnConfig = { sx: { width: [null, null, null, "130px"] } };

    const status: ColumnConfig = {
      sx: { width: [null, null, null, "130px"], justifyContent: [null, null, null, "flex-end"] },
    };

    return { period, worker, client, time, status };
  }, [isManager]);

  return (
    <Flex flexDirection="column" gap="xs">
      <ListHeader>
        <ListCellsWrapper>
          <ListHeaderCell columnConfig={cols.period}>{t("TIME_REPORTS.LIST.PERIOD")}</ListHeaderCell>
          {isManager && <ListHeaderCell columnConfig={cols.worker}>{t("TIME_REPORTS.LIST.WORKER")}</ListHeaderCell>}
          <ListHeaderCell columnConfig={cols.client}>{t("TIME_REPORTS.LIST.CLIENT_HIRING_MANAGER")}</ListHeaderCell>
          <ListHeaderCell columnConfig={cols.time}>
            {isManager ? t("TIME_REPORTS.LIST.REPORTED_TIME_COST") : t("TIME_REPORTS.LIST.REPORTED_TIME")}
          </ListHeaderCell>
          <ListHeaderCell columnConfig={cols.status}>{t("TIME_REPORTS.LIST.STATUS")}</ListHeaderCell>
        </ListCellsWrapper>
      </ListHeader>

      <List>
        {timeReports.map(tr => (
          <ListItem key={tr.guid}>
            <ListCellsWrapper
              as={Link}
              to={{
                pathname: router.get("time-reports.details", { timeReportGuid: tr.guid }).path,
                state: currentLocationState,
              }}>
              <ListItemCell columnConfig={cols.period}>
                <Flex gap="xs" alignItems="center">
                  {hasCompanyManagerPrivileges() ? (
                    <LazyUserAvatar user={tr.worker} />
                  ) : (
                    <CompanyAvatar name={tr.client.name} img={tr.client.logo?.publicUrl ?? undefined} />
                  )}

                  <Flex flexDirection="column">
                    <Paragraph>
                      <DateRange from={tr.startDate} to={tr.endDate} />
                    </Paragraph>
                    <DottedList>
                      <DottedListItem>
                        <Paragraph color="n700">{tr.systemId}</Paragraph>
                      </DottedListItem>
                      {tr.version > 0 && (
                        <DottedListItem>
                          <Paragraph color="n700">
                            {t("TIME_REPORTS.LIST.VERSION", { version: tr.version + 1 })}
                          </Paragraph>
                        </DottedListItem>
                      )}
                    </DottedList>
                  </Flex>
                </Flex>
              </ListItemCell>

              {isManager && (
                <ListItemCell columnConfig={cols.worker}>
                  <Paragraph ellipsis>{formatFullName(tr.worker)}</Paragraph>
                </ListItemCell>
              )}

              <ListItemCell columnConfig={cols.client}>
                <Flex flexDirection="column">
                  <Paragraph ellipsis>{tr.client.name}</Paragraph>
                  <Paragraph color="n700" ellipsis>
                    {formatFullName(tr.hiringManager)}
                  </Paragraph>
                </Flex>
              </ListItemCell>

              <ListItemCell columnConfig={cols.time}>
                <Flex flexDirection="column">
                  <Paragraph>{formatTotalLoggedTime(tr.totalReportedMinutes, tr.totalReportedDays)}</Paragraph>

                  {isManager && tr.totalReportedCost && (
                    <Paragraph color="n700">
                      {formatCost(tr.totalReportedCost, tr.currency.code, { maximumFractionDigits: 2 })}
                    </Paragraph>
                  )}
                </Flex>
              </ListItemCell>

              <ListItemCell columnConfig={cols.status}>
                <TimeReportStageStatus stage={tr.stage.code} />
              </ListItemCell>
            </ListCellsWrapper>
          </ListItem>
        ))}
      </List>
    </Flex>
  );
};
